import { Navigate, Link, useLocation } from "react-router-dom";
import cities from "@/data/cities.data.json";
import serviceLocations from "@/data/service-locations.data.json";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQ from "@/components/FAQ";
import { MapPin, ArrowRight, Sparkles } from "lucide-react";

const SITE = "https://www.spectrumservice.it";

export default function CityLanding() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "").split("/")[0];
  const city = cities.find((c) => c.slug === slug);
  if (!city) return <Navigate to="/" replace />;

  const related = serviceLocations.filter((s) => s.city_slug === city.slug);
  const canonical = `${SITE}/${city.slug}`;
  const title = `${city.hero_title} | Spectrum Service`;
  const description = city.hero_sub;

  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${canonical}#localbusiness`,
    name: `Spectrum Service — ${city.name}`,
    description: city.intro,
    url: canonical,
    telephone: "+39 388 8143630",
    email: "spectrumvalentin@gmail.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: city.province,
      addressCountry: "IT",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: city.geo.lat,
      longitude: city.geo.lon,
    },
    areaServed: { "@type": "City", name: city.name },
    priceRange: "€€",
  };

  return (
    <div data-testid={`city-landing-${city.slug}`} className="min-h-screen">
      <SEO title={title} description={description} canonical={canonical} type="website" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessLd) }}
      />

      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: city.name },
        ]}
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-3 flex items-center gap-1.5">
          <MapPin size={12} /> {city.name}, {city.province}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
          {city.hero_title.split(city.name)[0]}
          <span className="text-rose-500">{city.name}</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
          {city.hero_sub}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            data-testid={`city-cta-preventivo-${city.slug}`}
            to="/preventivo"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-mono uppercase tracking-widest transition"
          >
            Preventivo 24h <ArrowRight size={14} />
          </Link>
          <Link
            to="/catalogo"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-xs font-mono uppercase tracking-widest transition"
          >
            Vedi il catalogo
          </Link>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
          Perché Spectrum a {city.name}
        </div>
        <div className="text-zinc-300 leading-relaxed mb-6">{city.intro}</div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {city.highlights.map((h, i) => (
            <li
              key={i}
              className="flex items-start gap-2 bg-zinc-900/50 border border-zinc-800 rounded-lg p-3"
            >
              <Sparkles size={14} className="text-rose-400 shrink-0 mt-0.5" />
              <span className="text-sm text-zinc-200">{h}</span>
            </li>
          ))}
        </ul>
      </section>

      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 py-14 border-t border-zinc-900">
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
            Servizi
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase text-white mb-6">
            Cosa noleggiamo a {city.name}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map((s) => (
              <Link
                key={s.slug}
                data-testid={`city-service-link-${s.slug}`}
                to={`/servizi/${s.slug}`}
                className="block bg-zinc-900/60 border border-zinc-800 hover:border-rose-500/50 rounded-xl p-5 transition group"
              >
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Da €{s.price_from}
                </div>
                <div className="font-heading font-bold text-white group-hover:text-rose-400 transition">
                  {s.h1}
                </div>
                <div className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{s.intro}</div>
                <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-rose-400 flex items-center gap-1">
                  Scopri <ArrowRight size={11} />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {city.faqs && city.faqs.length > 0 && (
        <FAQ title={`Domande frequenti — ${city.name}`} items={city.faqs} />
      )}
    </div>
  );
}
